/**
 * D4 preset 风格库(设计稿 §4.3:风格做成一等选项,来源三选一 auto/preset/manual)。
 *
 * 每个 preset = 一份完整 styleTokens(palette 7 槽位 + 材质/光影/调性/密度),
 * 与 ai 双驱动同源:选中即整份替换(source="preset"),AI 图块 prompt DNA 与
 * PIL 矢量配色同时生效 —— 换 preset 后整图主色应 = preset accent(smoke 像素判据)。
 *
 * ═══ 2026-07-30:补两套深色底(dark_tech / dark_cinema)═══════════════════════
 * 原来 6 套的 `bg` 全是近白、`ink` 全是近黑。而画风库里有 4 个画风的 tone 文本
 * 明写深色基底(数据大屏「石墨黑深色基底」/ 赛博朋克「深靛蓝与石墨黑为基底」/
 * 科幻大片「冷蓝紫深空为基底」/ 机甲高达「冷灰基底」),它们只能借最近的浅色板 ——
 * 于是同一段【风格】里「石墨黑深色基底」和「背景 #F8FAFF」当场对撞,
 * 而且 `文字色 #0F172A` 配深底本身就是不可读的组合(线上任务 4740607640576)。
 *
 * ★深色板的 `ink` 必须是浅色 —— 这不是审美是物理:长图那条腿 `bands.palette_from_tokens`
 *   把 `ink` 直接当矢量文字色画在 `bg` 上,深底深字就是一片什么都看不见的色块。
 *   `toneToTokens.test.ts` 的对比度闸钉着这条,不靠自觉。
 */
import type { StyleTokens } from "./canvas";

export interface StyleTokenPreset {
  key: string;
  label: string;
  /** 一句话给小白的选择提示。 */
  hint: string;
  tokens: StyleTokens;
}

const preset = (
  key: string,
  label: string,
  hint: string,
  palette: StyleTokens["palette"],
  rest: Pick<StyleTokens, "material" | "lighting" | "tone" | "density">
): StyleTokenPreset => ({
  key,
  label,
  hint,
  tokens: {
    palette,
    ...rest,
    fontPair: { title: "modern_sans", body: "modern_sans" },
    source: "preset",
  },
});

export const STYLE_TOKEN_PRESETS: StyleTokenPreset[] = [
  preset(
    "premium_beauty",
    "高端美妆",
    "玫瑰金 × 磨砂柔光,贵妇质感",
    {
      primary: "#B76E79",
      secondary: "#F8F1EC",
      accent: "#B76E79",
      ink: "#2C2226",
      muted: "#8A7A80",
      line: "#E8DCD6",
      bg: "#FDF9F6",
    },
    { material: "frosted", lighting: "soft", tone: "premium_minimal", density: "airy" }
  ),
  preset(
    "tech_3c",
    "3C 科技",
    "深空蓝 × 金属冷光,参数感强",
    {
      primary: "#2563EB",
      secondary: "#EEF2FF",
      accent: "#2563EB",
      ink: "#0F172A",
      muted: "#64748B",
      line: "#DBE3F0",
      bg: "#F8FAFF",
    },
    { material: "metallic", lighting: "studio", tone: "tech_cool", density: "compact" }
  ),
  preset(
    "food_promo",
    "食品促销",
    "暖橙 × 高饱和,食欲与优惠感",
    {
      primary: "#EA580C",
      secondary: "#FFF3E8",
      accent: "#EA580C",
      ink: "#3B2416",
      muted: "#9A7B62",
      line: "#F3DFCC",
      bg: "#FFFBF6",
    },
    { material: "glossy", lighting: "natural", tone: "vivid_promo", density: "normal" }
  ),
  preset(
    "home_warm",
    "家居生活",
    "原木暖调 × 自然光,温馨生活场",
    {
      primary: "#A16207",
      secondary: "#F7F2E9",
      accent: "#A16207",
      ink: "#292018",
      muted: "#8C8072",
      line: "#E9E0D2",
      bg: "#FCFAF5",
    },
    { material: "natural", lighting: "natural", tone: "warm_life", density: "normal" }
  ),
  preset(
    "fresh_mom_baby",
    "母婴清新",
    "薄荷奶绿 × 柔光,干净安心",
    {
      primary: "#0D9488",
      secondary: "#ECFAF7",
      accent: "#0D9488",
      ink: "#1B2A28",
      muted: "#6C8985",
      line: "#D6EEE9",
      bg: "#F9FEFC",
    },
    { material: "matte", lighting: "soft", tone: "fresh_clean", density: "airy" }
  ),
  preset(
    "sport_outdoor",
    "运动户外",
    "活力红 × 戏剧光,力量与冲击",
    {
      primary: "#DC2626",
      secondary: "#FDF0EF",
      accent: "#DC2626",
      ink: "#1C1917",
      muted: "#78716C",
      line: "#EBDBD8",
      bg: "#FFFAF9",
    },
    { material: "matte", lighting: "dramatic", tone: "vivid_promo", density: "compact" }
  ),
  // ── 深色底两套(2026-07-30)——见文件头 ────────────────────────────────
  preset(
    "dark_tech",
    "深色科技",
    "石墨黑 × 电光青,数据与机甲的冷硬感",
    {
      primary: "#22D3EE",
      secondary: "#1E293B",
      accent: "#22D3EE",
      ink: "#E8EEF7",
      muted: "#94A3B8",
      line: "#334155",
      bg: "#0F172A",
    },
    { material: "metallic", lighting: "studio", tone: "tech_cool", density: "compact" }
  ),
  preset(
    "dark_cinema",
    "深色影调",
    "深空靛紫 × 暖金,电影级夜景",
    {
      primary: "#818CF8",
      secondary: "#232544",
      accent: "#F5B544",
      ink: "#EDEAF6",
      muted: "#9E9AB8",
      line: "#3A3B60",
      bg: "#12132A",
    },
    { material: "glossy", lighting: "dramatic", tone: "tech_cool", density: "normal" }
  ),
];

export function findStyleTokenPreset(key: string): StyleTokenPreset | undefined {
  return STYLE_TOKEN_PRESETS.find((p) => p.key === key);
}

/**
 * 深拷一份 tokens。
 *
 * ★预设是**模块级共享常量**:直接把 `preset.tokens` 塞进 state,那份"产物配色"
 * 就成了一个全局可变对象 —— 谁在哪儿就地改一个色位(哪怕只是将来某个人图省事),
 * 全站所有用过这个预设的地方跟着变,且只在运行时才看得出来。
 * 浅拷(`{...preset.tokens}`)不够:`palette` / `fontPair` 还是同一个引用。
 */
export function copyStyleTokens(t: StyleTokens): StyleTokens {
  return { ...t, palette: { ...t.palette }, fontPair: { ...t.fontPair } };
}

/**
 * 反查:这份 tokens 是不是原封不动的某个预设(V0)。
 *
 * ★收口成一个函数,是因为它有**两个消费方**:配色面板的高亮态、详情页「本次设置」里
 * 记的配色名。两处各写一遍判据必然漂 —— 面板高亮着「食品促销」、记录写另一个名字,
 * 就是「概览比被概览的更不可信」那个老账。
 *
 * 判据:`source==="preset"` + palette 七槽逐字相同 + 四个枚举全同。
 * 只要用户动过任何一项(source 转 manual)或画风推导时改过质感,就**不再命中** ——
 * 微调完还高亮原预设是在撒谎。
 */
export function matchStyleTokenPreset(
  tokens: StyleTokens | null | undefined
): StyleTokenPreset | undefined {
  if (!tokens || tokens.source !== "preset") return undefined;
  return STYLE_TOKEN_PRESETS.find(
    (p) =>
      (Object.keys(p.tokens.palette) as Array<keyof StyleTokens["palette"]>).every(
        (k) => p.tokens.palette[k] === tokens.palette[k]
      ) &&
      p.tokens.tone === tokens.tone &&
      p.tokens.material === tokens.material &&
      p.tokens.lighting === tokens.lighting &&
      p.tokens.density === tokens.density
  );
}
